import { useState, useEffect } from "react";
import { api } from "../../../lib/api";
import { fmtDT, fmtINR } from "../../../lib/constants";
import { CommercialChip } from "../../../components/CommercialChip";
import { toast } from "sonner";
import {
  Receipt, CurrencyInr, CheckCircle, ShieldCheck, Lock,
  ArrowClockwise, HourglassMedium,
} from "@phosphor-icons/react";
import { AcceptedQuoteBreakdown, GateChip, Info, Panel, friendlyError } from "./_shared";
import { COMPANY_INACTIVE_MESSAGE, isCompanyInactive } from "../../../components/RequireSenderCompany";

// P2-33 (Founder-locked) — captured MAIN/DELTA amount differs from the booking: manual, never automatic.
const AMOUNT_MISMATCH_MSG = "Payment received, but the amount does not match this booking. Please contact Air12 Support before making another payment.";

// Load the Razorpay Standard Checkout script once (idempotent).
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById("razorpay-checkout-js");
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-checkout-js";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ---------------------------------------------------------------------------------
// Pickup Operating Calendar — Sender pre-MAIN recovery. Shown ONLY when MAIN checkout
// is blocked by the backend (409 PICKUP_TIME_NO_LONGER_AVAILABLE / PICKUP_TIME_PASSED).
// Backend stays authoritative: choices/slots come from the availability API and the
// replacement is saved through the recovery-only PATCH. No reprice, no new Quote, and
// (Founder-locked) NO automatic Razorpay re-open — the Sender clicks MAIN again explicitly.
// ---------------------------------------------------------------------------------
function PickupRecoveryBlock({ shipmentId, routeCode, message, onResolved }) {
  const [avail, setAvail] = useState(null);
  const [sd, setSd] = useState("");
  const [slot, setSlot] = useState("");
  const [expMsg, setExpMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/booking/pickup-availability`, { params: { route_code: routeCode } });
      setAvail(data);
      // Clear any selection that is no longer one of the currently offered choices/slots.
      const choices = [data.today, data.next_available].filter(Boolean);
      const match = choices.find((c) => c.date === sd);
      if (!(match && match.slots.includes(slot))) { setSd(""); setSlot(""); }
    } catch (e) { setAvail(null); }
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCode]);

  useEffect(() => {
    if (!avail || !sd || !slot) { setExpMsg(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/booking/pickup-availability`, {
          params: { route_code: routeCode, service_date: sd, slot },
        });
        if (alive) setExpMsg(data.expected_delivery?.message || null);
      } catch (e) { if (alive) setExpMsg(null); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avail, sd, slot]);

  const choices = avail ? [avail.today, avail.next_available].filter(Boolean) : [];
  const chosen = choices.find((c) => c.date === sd);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/booking/${shipmentId}/pickup`, { service_date: sd, preferred_pickup_window: slot });
      toast.success("Pickup time updated. Please continue to payment.");
      onResolved();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = det?.code;
      if (code === "PAYMENT_ALREADY_COMPLETED" || code === "PICKUP_STILL_VALID") {
        // No longer requires recovery (or already paid) — surface cleanly and close recovery.
        toast.message(det?.message || "This pickup no longer needs changing.");
        onResolved();
      } else {
        // Availability may have changed again — surface the backend message and refresh choices.
        toast.error(det?.message || friendlyError(e, "We couldn't update the pickup time. Please try again."));
        await refresh();
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 px-3 py-3 rounded bg-[hsl(var(--warning)/0.08)] border border-[hsl(var(--warning)/0.4)]" data-testid="pickup-recovery-block">
      <p className="text-xs text-[hsl(var(--danger))] font-medium" data-testid="pickup-recovery-notice">{message}</p>
      <div>
        <div className="overline mb-1">PICKUP DATE</div>
        <div className="flex flex-col gap-2" data-testid="pickup-recovery-date-choices">
          {choices.map((c) => (
            <label key={c.date} className="flex items-center gap-2 text-sm">
              <input type="radio" name={`pickup-recovery-${shipmentId}`} value={c.date}
                checked={sd === c.date}
                onChange={() => { setSd(c.date); setSlot(""); }}
                data-testid={`pickup-recovery-date-${c.date}`} />
              <span>{c.label}</span>
            </label>
          ))}
          {!avail && <p className="text-xs text-[hsl(var(--muted))]">Loading pickup availability…</p>}
        </div>
      </div>
      <label className="block text-sm">
        <span className="overline">PICKUP TIME</span>
        <select className="input-field mt-1" value={slot} disabled={!sd}
          onChange={(e) => setSlot(e.target.value)} data-testid="pickup-recovery-time-select">
          <option value="">{sd ? "Select a pickup time" : "Select a pickup date first"}</option>
          {(chosen ? chosen.slots : []).map((sopt) => (<option key={sopt} value={sopt}>{sopt}</option>))}
        </select>
      </label>
      {expMsg && <p className="text-xs text-[hsl(var(--muted))]" data-testid="pickup-recovery-expected-delivery">{expMsg}</p>}
      <button className="btn-primary text-sm" disabled={saving || !sd || !slot} onClick={save} data-testid="pickup-recovery-save">
        {saving ? "Saving…" : "Save new pickup time"}
      </button>
      <p className="text-[11px] text-[hsl(var(--muted))]">Pickup times are shown in India Standard Time (IST). After saving, select Pay again to continue.</p>
    </div>
  );
}

// ==================================================================================
// P3 COMMERCIAL LIFECYCLE
//   1. Sender pays ONE Air12 amount (insurance folded pre-GST inside MAIN).
//   2. After MAIN COMPLETED → Air12 (Source CTOP / Super Admin) records + confirms insurance.
//      System-derived premium/coverage; operator supplies insurer, reference + evidence.
//   3. After insurance CONFIRMED → Sender acknowledges the final SDA/MIC declarations
//      (handled by FinalDeclarationPanel), then authoritative documents generate.
// Sender never submits insurance. No manual quote. No manual delta settlement. No requote.
// ==================================================================================

// A revised Quote version must be explicitly accepted (its own Quote Acceptance Record) before payment.
function QuoteVersionAcceptBlock({ shipmentId, qa, payable, onAccepted }) {
  const [name, setName] = useState("");
  const [affirmed, setAffirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const valid = affirmed && /^[A-Za-z. ]+$/.test(name.trim());
  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/booking/${shipmentId}/accept-quote-version`, {
        quote_id: qa.quote_id, quote_authority_affirmed: true, accepting_person_name: name.trim(),
      }, { timeout: 90000 });
      toast.success(`Quote ${qa.public_quote_id} accepted.`);
      onAccepted();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || "The revised quote could not be accepted. Please retry.");
      if (det?.code === "QUOTE_VERSION_STALE") onAccepted();
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 border rounded-sm p-3" data-testid="quote-version-accept-block">
      <div className="text-sm font-semibold" data-testid="revised-quote-label">Revised quote {qa.public_quote_id} (version {qa.version}) · {fmtINR(payable)}</div>
      <p className="text-xs text-[hsl(var(--muted))]">Your quote was revised. Please review and accept this version before paying.</p>
      <input className="input-field w-full" value={name} placeholder="Full legal name of the authorised person"
             onChange={(e) => setName(e.target.value.replace(/[^A-Za-z. ]/g, ""))} data-testid="revised-quote-name-input" />
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={affirmed} onChange={(e) => setAffirmed(e.target.checked)} data-testid="revised-quote-affirm-checkbox" />
        <span>{qa.affirmation_text}</span>
      </label>
      <button className="btn-primary" disabled={busy || !valid} onClick={accept} data-testid="revised-quote-accept-button">
        {busy ? "Accepting…" : qa.action_label}
      </button>
    </div>
  );
}

export function CommercialGatePanel({ d, reload, profile, isAdmin }) {
  // isAdmin === canActSource (assigned Source CTOP or Super Admin) — the post-payment insurance operator.
  const s = d.shipment;
  const activeQuote = (d.quotes || []).find((q) => q.is_active) || (d.quotes || [])[0];
  const isSender = profile?.role === "SENDER";
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null); // {code, message} when MAIN blocked on pickup
  // A13 — Captains / Riders: payment + insurance status labels only (no quote, no amounts).
  const statusOnly = ["CAP", "RID"].includes(profile?.role);
  if (!activeQuote && !statusOnly) return null;

  const payStatus = s.air12_payment_status;
  const insStatus = s.insurance_status || "PENDING";
  const payCompleted = payStatus === "COMPLETED";
  const payPending = payStatus === "PENDING";
  const insConfirmed = insStatus === "CONFIRMED";
  const payable = activeQuote ? (activeQuote.payable != null ? activeQuote.payable : activeQuote.total_payable) : null;
  // Human-readable operational wording (never expose the raw payment/insurance enum to any role).
  // Sender-facing PENDING is deliberately neutral: PENDING is set the moment the Razorpay Order is
  // created, so it can mean checkout opened/dismissed/failed-auth OR a real payment still awaiting
  // reconciliation. It is NOT an authoritative failure — never say "failed".
  const payLabel = (isSender && payPending)
    ? "Payment not yet confirmed"
    : ({ UNLOCKED: "Awaiting payment", PENDING: "Submitted — verifying", COMPLETED: "Paid" }[payStatus] || "Awaiting payment");
  const insLabel = { PENDING: "Processing", CONFIRMED: "Confirmed", REJECTED: "Rejected" }[insStatus] || "Processing";

  // Sender self-cancellation is handled by the dedicated CancellationPanel (AIR12-CXL), which
  // uses the authoritative preview/fingerprint flow and supports pre- and post-payment stages.

  const submitPayment = async () => {
    setBusy(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("We couldn't load the secure payment window. Check your connection and try again.");
        return;
      }
      const { data } = await api.post(`/booking/${s.id}/main-payment/checkout`);
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: data.currency,
        name: data.name,
        description: data.description,
        handler: async (resp) => {
          try {
            const { data: v } = await api.post(`/booking/${s.id}/main-payment/verify-callback`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (v.status === "COMPLETED") toast.success("Payment confirmed — booking is now frozen.");
            else if (v.outcome === "AMOUNT_MISMATCH") toast.error(AMOUNT_MISMATCH_MSG);
            else toast.message("Payment received — finalising your booking. This updates automatically.");
          } catch (e) {
            toast.error(friendlyError(e, "We couldn't confirm your payment yet. If it went through, the status will update automatically."));
          } finally {
            reload();
          }
        },
        modal: { ondismiss: () => { setBusy(false); reload(); } },
        theme: { color: "#0f766e" },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment was not completed. Please try again.");
      });
      rzp.open();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = det?.code;
      if (e?.response?.status === 409 && (code === "PICKUP_TIME_NO_LONGER_AVAILABLE" || code === "PICKUP_TIME_PASSED")) {
        // Backend blocked MAIN before any Razorpay order — enter pickup recovery (no checkout opened).
        setRecovery({ code, message: det.message });
        return;
      }
      toast.error(friendlyError(e, "We couldn't start the payment. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel icon={<ShieldCheck size={18} />} title="COMMERCIAL — AIR12 PAYMENT & INSURANCE">
      <div data-testid="commercial-gate-panel" className="space-y-4">
        <div className="flex justify-end -mb-1">
          <CommercialChip shipment={s} role={profile?.role} />
        </div>

        {/* Status strip (everyone) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          <GateChip label="AIR12 PAYMENT" value={payLabel} tone={payCompleted ? "ok" : payPending ? "warn" : "muted"} testid="gate-payment-status" />
          <GateChip label="INSURANCE" value={payCompleted ? insLabel : "After payment"} tone={insConfirmed ? "ok" : insStatus === "REJECTED" ? "bad" : "warn"} testid="gate-insurance-status" />
          {!statusOnly && <GateChip label="TOTAL PAYABLE" value={fmtINR(payable)} sub="incl. insurance & GST" tone="ok" testid="gate-air12-amount" />}
        </div>

        {/* Single Air12 payment breakdown — insurance is a pre-GST line INSIDE the total */}
        {!statusOnly && <AcceptedQuoteBreakdown quote={activeQuote} showPricingVersion={!isSender} />}

        {/* ---------------- SENDER ---------------- */}
        {isSender && !payCompleted && isCompanyInactive(profile) && (
          <div className="px-3 py-2 rounded bg-[hsl(var(--warning)/0.1)] border border-[hsl(var(--warning)/0.4)] text-xs" data-testid="company-inactive-payment-note">
            <span className="font-semibold">Your company is currently inactive.</span> {COMPANY_INACTIVE_MESSAGE}
          </div>
        )}
        {isSender && !payCompleted && !isCompanyInactive(profile) && (
          <div className="space-y-3" data-testid="sender-checkout">
            {payPending && (
              <div className="px-3 py-2 rounded bg-[hsl(var(--warning)/0.1)] border border-[hsl(var(--warning)/0.4)] text-xs" data-testid="payment-recovering-note">
                If you cancelled or your payment did not complete, you can retry securely. If you
                already completed payment, Air12 will confirm it automatically.
              </div>
            )}
            <div data-testid="razorpay-checkout" className="space-y-2">
              <div className="overline">PAY AIR12 · {fmtINR(payable)}</div>
              {isSender && s.quote_acceptance_required ? (
                <QuoteVersionAcceptBlock shipmentId={s.id} qa={s.quote_acceptance} payable={payable} onAccepted={reload} />
              ) : !s.has_goods_invoice ? (
                <div className="px-3 py-2 rounded bg-[hsl(var(--warning)/0.1)] border border-[hsl(var(--warning)/0.4)] text-xs" data-testid="goods-invoice-required-note">
                  Please upload your <span className="font-semibold">Goods Invoice</span> to enable payment.
                </div>
              ) : recovery ? (
                <PickupRecoveryBlock
                  shipmentId={s.id}
                  routeCode={s.route_code}
                  message={recovery.message}
                  onResolved={() => { setRecovery(null); reload(); }}
                />
              ) : (
                <button className="btn-primary w-full sm:w-auto" disabled={busy} onClick={submitPayment} data-testid="pay-now-button">
                  {busy ? "Opening secure checkout…" : `Pay ${fmtINR(payable)} securely`}
                </button>
              )}
              <p className="text-[11px] text-[hsl(var(--muted))]">
                Secure payment powered by Razorpay. Insurance is included in your Air12 payment. After
                payment, Air12 sends the insurance request to our insurance partner (PolicyBazaar).
              </p>
            </div>
          </div>
        )}

        {isSender && payCompleted && !insConfirmed && (
          <div className="px-3 py-3 rounded bg-[hsl(var(--accent)/0.08)] border border-[hsl(var(--accent)/0.3)] text-sm" data-testid="sender-insurance-processing">
            {s.insurance_request_sent ? (
              <>
                <div className="font-semibold flex items-center gap-1.5" data-testid="insurance-request-sent"><HourglassMedium size={16} /> Insurance request sent — awaiting confirmation</div>
                <p className="text-xs text-[hsl(var(--muted))] mt-1">
                  Your payment is confirmed and your booking is frozen. Air12 has sent the insurance
                  request to PolicyBazaar. Airline handover proceeds once insurance is completed.
                </p>
              </>
            ) : (
              <>
                <div className="font-semibold flex items-center gap-1.5" data-testid="insurance-request-preparing"><HourglassMedium size={16} /> Booking Confirmed — Insurance In Progress</div>
                <p className="text-xs text-[hsl(var(--muted))] mt-1">
                  Your payment is confirmed and your booking is frozen. Air12 is preparing your cargo
                  insurance request. Airline handover proceeds once insurance is completed.
                </p>
              </>
            )}
          </div>
        )}

        {isSender && insConfirmed && (
          <div className="grid-card p-3 text-sm" data-testid="insurance-completed-summary">
            <div className="overline mb-1">CARGO INSURANCE — COMPLETED</div>
            <Info label="COMPLETED">{fmtDT(s.insurance_confirmed_at)}</Info>
          </div>
        )}

        {/* ---------------- SOURCE CTOP / SUPER ADMIN — insurance completion ---------------- */}
        {isAdmin && payCompleted && (
          <InsuranceCompletionBlock d={d} insConfirmed={insConfirmed} reload={reload} profile={profile} />
        )}
        {isAdmin && !payCompleted && (
          <div className="text-xs text-[hsl(var(--muted))] pt-2 border-t border-[hsl(var(--border))]" data-testid="insurance-ops-locked">
            Waiting for MAIN payment. Insurance is handled by email with PolicyBazaar after the sender's Air12 payment is completed.
          </div>
        )}
      </div>
    </Panel>
  );
}

// ---- Source CTOP — Mark insurance completed (Insurance Operational Closure) ----
// The assigned Source CTOP confirms, after checking the external PolicyBazaar email trail, that
// insurance is completed. No insurer/policy/evidence entry. Super Admin has read-only visibility.
function InsuranceCompletionBlock({ d, insConfirmed, reload, profile }) {
  const s = d.shipment;
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The assigned Source CTOP is the normal owner; SUPER_ADMIN may also perform the SAME action
  // (parity). Every other role is read-only here.
  const canMarkCompleted = s.current_status !== "CANCELLED" && (profile?.role === "SUPER_ADMIN"
    || (profile?.role === "CTOP" && s.source_ctop_id === profile?.id));

  const markCompleted = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${s.id}/insurance/mark-completed`);
      toast.success("Insurance marked completed");
      setConfirmOpen(false);
      reload();
    } catch (e) { toast.error(friendlyError(e, "Could not mark insurance completed")); }
    finally { setBusy(false); }
  };

  if (insConfirmed) {
    return (
      <div className="space-y-3 pt-2 border-t border-[hsl(var(--border))]" data-testid="insurance-completed-block">
        <div className="grid-card p-3 text-sm" data-testid="insurance-completed-summary-admin">
          <div className="overline mb-1">CARGO INSURANCE — COMPLETED</div>
          <div className="flex items-center gap-2 text-[hsl(var(--muted))]">
            <Lock size={13} /> Completed on {fmtDT(s.insurance_confirmed_at)} — locked.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-2 border-t border-[hsl(var(--border))]" data-testid="insurance-pending-block">
      <div className="overline">CARGO INSURANCE — PENDING</div>
      <p className="text-xs text-[hsl(var(--muted))]">
        Air12 has emailed the insurance request to PolicyBazaar (CC Air12). PolicyBazaar handles the
        process by email. After confirming the external email trail, the assigned Source CTOP marks
        insurance completed. Airline handover stays blocked until then.
      </p>
      {canMarkCompleted ? (
        !confirmOpen ? (
          <button className="btn-primary text-xs" disabled={busy} onClick={() => setConfirmOpen(true)} data-testid="mark-insurance-completed-button">
            <CheckCircle size={14} /> Mark insurance completed
          </button>
        ) : (
          <div className="grid-card p-3 space-y-2" data-testid="mark-insurance-confirm-dialog">
            <p className="text-xs">Confirm the PolicyBazaar insurance process is completed for this shipment? This cannot be reversed.</p>
            <div className="flex gap-2">
              <button className="btn-primary text-xs" disabled={busy} onClick={markCompleted} data-testid="mark-insurance-confirm-button">Yes, mark completed</button>
              <button className="btn-ghost text-xs" disabled={busy} onClick={() => setConfirmOpen(false)} data-testid="mark-insurance-cancel-button">Cancel</button>
            </div>
          </div>
        )
      ) : (
        <div className="text-[11px] text-[hsl(var(--muted))]" data-testid="insurance-readonly-note">
          Only the assigned Source CTOP or a Super Admin can mark insurance completed.
        </div>
      )}
    </div>
  );
}

// AIR12 — Sender view of the balance (DELTA) payable after final airport verification, with the
// preview payment action. Reads the role-shaped commercial state from GET /checkout/{id}/delta.
export function SenderDeltaPanel({ d, profile }) {
  const isSender = profile?.role === "SENDER";
  const s = d.shipment;
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/checkout/${s.id}/delta`);
      setState(data);
    } catch (e) { setState(null); }
  };
  useEffect(() => {
    if (isSender) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id, isSender]);

  if (!isSender || !state || !state.finalized) return null;
  const amount = Number(state.delta_amount || 0);
  if (amount <= 0) {
    return (
      <Panel icon={<CurrencyInr size={16} weight="bold" />} title="FINAL AIRPORT VERIFICATION">
        <div className="text-xs text-[hsl(var(--accent))]" data-testid="sender-delta-none">
          Final airport verification is complete. No additional payment is due.
        </div>
      </Panel>
    );
  }
  const settled = !!state.settled;
  const cur = state.current_attempt;
  const status = settled ? (cur && cur.status === "WAIVED" ? "WAIVED" : "PAID") : (cur ? cur.status : "PENDING");
  const processing = !settled && status === "PROCESSING";
  const canPay = !!state.can_pay;

  const pay = async () => {
    setBusy(true); setFailed(false);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("We couldn't load the secure payment window. Check your connection and try again.");
        return;
      }
      const { data } = await api.post(`/checkout/${s.id}/delta`, {});
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency,
        name: data.name, description: data.description,
        handler: async (resp) => {
          try {
            const { data: v } = await api.post(`/checkout/${s.id}/delta/verify-callback`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (v.status === "SETTLED") toast.success("Balance payment confirmed.");
            else if (v.outcome === "AMOUNT_MISMATCH") toast.error(AMOUNT_MISMATCH_MSG);
            else toast.message("Payment received — finalising. This updates automatically.");
          } catch (e) {
            setFailed(true);
            toast.error(friendlyError(e, "We couldn't confirm your payment yet. If it went through, the status will update automatically."));
          } finally { await load(); }
        },
        modal: { ondismiss: () => { setBusy(false); load(); } },
        theme: { color: "#0f766e" },
      });
      rzp.on("payment.failed", () => { setFailed(true); toast.error("Payment was not completed. Please try again."); });
      rzp.open();
    } catch (e) {
      setFailed(true);
      toast.error(friendlyError(e, "We couldn't start the payment. Please try again."));
    } finally { setBusy(false); }
  };

  return (
    <Panel icon={<CurrencyInr size={16} weight="bold" />} title="BALANCE PAYMENT (AFTER FINAL VERIFICATION)">
      <div data-testid="sender-delta-panel" className="space-y-3">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="grid-card p-3">
            <div className="overline">BALANCE PAYABLE</div>
            <div className="heading text-xl mono mt-1" data-testid="sender-delta-amount">{fmtINR(amount)}</div>
          </div>
          <div className="grid-card p-3">
            <div className="overline">STATUS</div>
            <div className="mt-1" data-testid="sender-delta-status">
              {status === "PAID" ? <span className="status-pill status-success">PAID</span>
                : status === "WAIVED" ? <span className="status-pill status-success">WAIVED</span>
                : processing ? <span className="status-pill status-warning">PROCESSING</span>
                : failed ? <span className="status-pill status-danger">PAYMENT NOT COMPLETED</span>
                : <span className="status-pill status-warning">BALANCE PAYMENT PENDING</span>}
            </div>
          </div>
        </div>
        {!settled ? (
          processing ? (
            <div className="text-xs text-[hsl(var(--warning))]" data-testid="sender-delta-processing">
              Payment is processing. Please wait for it to complete.
            </div>
          ) : failed ? (
            <div className="space-y-2" data-testid="sender-delta-failed-block">
              <p className="text-xs text-[hsl(var(--danger))]" data-testid="sender-delta-failed-note">
                Payment was not completed. Please retry the balance payment.
              </p>
              <button className="btn-primary text-sm w-full" disabled={busy} onClick={pay} data-testid="sender-delta-retry-button">
                {busy ? "Processing…" : "Retry payment"}
              </button>
            </div>
          ) : (
            canPay ? (
              <div className="space-y-2" data-testid="sender-delta-pay-block">
                <p className="text-xs text-[hsl(var(--muted))]">
                  Final airport verification changed the payable amount. Airline handover proceeds once this balance is paid.
                </p>
                <button className="btn-primary text-sm w-full" disabled={busy} onClick={pay} data-testid="sender-delta-pay-button">
                  {busy ? "Opening secure checkout…" : `Pay balance ${fmtINR(amount)} securely`}
                </button>
                <p className="text-[11px] text-[hsl(var(--muted))]">Secure payment powered by Razorpay.</p>
              </div>
            ) : (
              <div className="text-xs text-[hsl(var(--warning))]" data-testid="sender-delta-not-requested">
                A balance is due after final verification. Air12 will send you a payment request shortly.
              </div>
            )
          )
        ) : (
          <div className="text-xs text-[hsl(var(--accent))]" data-testid="sender-delta-done-note">
            {status === "WAIVED" ? "Balance payment waived. No further payment is required." : "Balance payment completed. Thank you."}
          </div>
        )}
      </div>
    </Panel>
  );
}

// Read-only quote card (manual quote creation retired — P3G). Kept for admin visibility only.
export function QuotePanel({ d }) {
  const active = (d.quotes || []).find((q) => q.is_active) || (d.quotes || [])[0];
  if (!active) return null;
  return (
    <Panel icon={<Receipt size={16} weight="bold" />} title={`QUOTE${active.public_quote_id ? ` · ${active.public_quote_id}` : ""}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="quote-readonly">
        <Info label="AIR12 SERVICE">{fmtINR((active.subtotal_excl_insurance != null ? active.subtotal_excl_insurance : active.movement_fee) - (active.concierge_service_fee || 0))}</Info>
        {active.concierge_service_fee > 0 && (
          <Info label="CONCIERGE SERVICE">{fmtINR(active.concierge_service_fee)}</Info>
        )}
        <Info label="INSURANCE (INCL.)">{fmtINR(active.insurance_premium || 0)}</Info>
        <Info label="GST">{fmtINR(active.gst_amount != null ? active.gst_amount : active.gst)}</Info>
        {Number(active.round_off) !== 0 && active.round_off != null && (
          <Info label="ROUND OFF">{`${Number(active.round_off) < 0 ? "− " : "+ "}${fmtINR(Math.abs(Number(active.round_off)))}`}</Info>
        )}
        <Info label="TOTAL PAYABLE">{fmtINR(active.payable != null ? active.payable : active.total_payable)}</Info>
      </div>
      {active.quote_source && (
        <div className="mt-2 text-[11px] text-[hsl(var(--muted))]" data-testid="quote-source">Auto-generated Air12 service quote</div>
      )}
      {d.company_identity && (
        <div className="mt-3 pt-3 border-t" data-testid="frozen-company-identity">
          <div className="overline text-[10px] mb-1.5">Company identity (frozen)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Info label="LEGAL NAME">{d.company_identity.company_legal_name || "—"}</Info>
            <Info label="PAN">{d.company_identity.pan_masked || "—"}</Info>
            <Info label="GST REGISTRATION">{d.company_identity.no_gstin ? "No GSTIN" : (d.company_identity.gstin_masked || "—")}</Info>
          </div>
        </div>
      )}
    </Panel>
  );
}

// Read-only payment ledger (manual "mark payment" retired — payments flow through the sender
// checkout + Source CTOP/SA verification in the commercial gate).
export function PaymentPanel({ d }) {
  return (
    <Panel icon={<CurrencyInr size={16} weight="bold" />} title="PAYMENTS">
      {d.payments.length > 0 ? (
        <table className="table-air12">
          <thead><tr><th>Pay ID</th><th>Type</th><th>Amount</th><th>Status</th><th>Ref</th><th>Paid At</th></tr></thead>
          <tbody>
            {d.payments.map((p) => {
              const typeLabel = { MAIN: "Air12 payment", DELTA: "Additional (delta)" }[p.payment_type] || p.payment_type;
              const statusLabel = { COMPLETED: "Completed", PENDING: "Awaiting verification", VOID: "Superseded", WAIVED: "Waived", FAILED: "Failed" }[p.status] || "Processing";
              return (
              <tr key={p.id} data-testid={`pay-row-${p.public_payment_id}`}>
                <td><span className="id-chip text-xs">{p.public_payment_id}</span></td>
                <td>{typeLabel}</td>
                <td className="mono">{fmtINR(p.amount)}</td>
                <td>{p.status === "COMPLETED" ? <span className="status-pill status-success">Completed</span> : statusLabel}</td>
                <td className="mono text-xs">{p.payment_reference || "—"}</td>
                <td className="mono text-xs">{fmtDT(p.paid_at)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      ) : <p className="text-sm text-[hsl(var(--muted))]" data-testid="no-payments">No payments yet.</p>}
    </Panel>
  );
}

// AIR12 — legacy DELTA waiver (old `/verification/delta/waive` route) retired. The SUPER_ADMIN
// waiver now lives in VerificationReviewPanel (PackagePanels) via POST /checkout/{id}/delta/waive.

